/**
 * Composant Modal de connexion/inscription
 * Gère l'authentification des utilisateurs avec Firebase
 * Permet la connexion, l'inscription et la réinitialisation du mot de passe
 */

import React, { useState, useEffect } from 'react';
// =============================================================
// Composant : LoginModal
// Objectif : centraliser les interactions d'authentification
//             (connexion, inscription, reset mot de passe)
//             avec un design très customisé.
// =============================================================
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase/config';
import { signInWithEmailAndPassword, signOut, sendPasswordResetEmail, User } from 'firebase/auth';
import { createUserProfile, getUserProfile } from '../firebase/userService';
import { Language } from '../types/user';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { validatePassword } from '../utils/passwordValidator';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Popover,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

/**
 * Types pour les props du composant
 */
interface LoginModalProps {
  isOpen: boolean;       // Contrôle l'affichage du modal
  onClose: () => void;   // Fonction appelée à la fermeture du modal
}

/**
 * @param {boolean} isOpen - État d'ouverture du modal
 * @param {Function} onClose - Fonction de fermeture du modal
 */
const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  // -------------------------------------------------------------
  // États d'UI globaux (mode actif, popover, acceptation CGU)
  // -------------------------------------------------------------
  // État qui contrôle l'affichage du formulaire d'inscription
  const [isSignUp, setIsSignUp] = useState(false);                
  // État qui contrôle l'affichage du formulaire de réinitialisation du mot de passe
  const [isForgotPassword, setIsForgotPassword] = useState(false); 
  // État qui contrôle l'affichage du popover de confirmation
  const [showConfirmationPopover, setShowConfirmationPopover] = useState(false);
  // État qui contrôle l'acceptation des CGU
  const [termsAccepted, setTermsAccepted] = useState(false);
  // État qui stocke l'élément HTML qui a déclenché le popover
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  
  // -------------------------------------------------------------
  // États des champs de formulaire (utiles pour retravailler le design
  // champ par champ : email, mot de passe, infos profil, etc.)
  // -------------------------------------------------------------
  // État qui stocke l'email saisi par l'utilisateur
  const [email, setEmail] = useState('');
  // État qui stocke le mot de passe saisi par l'utilisateur
  const [password, setPassword] = useState('');
  // État qui stocke la confirmation du mot de passe saisi par l'utilisateur
  const [confirmPassword, setConfirmPassword] = useState('');
  // État qui contrôle la visibilité du mot de passe
  const [showPassword, setShowPassword] = useState(false);        
  // État qui stocke la langue sélectionnée par l'utilisateur
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('en');  
  const [age, setAge] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  
  // -------------------------------------------------------------
  // Indicateur de validation du mot de passe (affichage du widget de force)
  // -------------------------------------------------------------
  const [passwordValidation, setPasswordValidation] = useState(validatePassword(''));
  
  // -------------------------------------------------------------
  // Messages systèmes (erreurs / succès affichés en tête du formulaire)
  // -------------------------------------------------------------
  const [error, setError] = useState('');                        // Messages d'erreur
  const [successMessage, setSuccessMessage] = useState('');      // Messages de succès
  
  // -------------------------------------------------------------
  // Contexte Auth : fournit signin / signup / etc.
  // -------------------------------------------------------------
  const { signin, signup } = useAuth();

  /**
   * Fonction pour vérifier l'état de l'email
   */
  const checkEmailVerification = async () => {
    const pendingEmail = localStorage.getItem('pendingVerification');
    if (pendingEmail) {
      try {
        const storedPassword = localStorage.getItem('tempPassword');
        if (storedPassword) {
          const userCredential = await signInWithEmailAndPassword(auth, pendingEmail, storedPassword);
          await userCredential.user.reload();
          
          if (userCredential.user.emailVerified) {
            setIsSignUp(false);
            setSuccessMessage(t('auth.emailVerified'));
            setEmail(pendingEmail);
            const pendingLanguage = localStorage.getItem('tempLanguage') as Language | null;
            const pendingAge = localStorage.getItem('tempAge');
            const pendingCountry = localStorage.getItem('tempCountry');
            const pendingCity = localStorage.getItem('tempCity');
            if (pendingLanguage) {
              setSelectedLanguage(pendingLanguage);
            }
            if (pendingAge) {
              setAge(pendingAge);
            }
            if (pendingCountry) {
              setCountry(pendingCountry);
            }
            if (pendingCity) {
              setCity(pendingCity);
            }
            localStorage.removeItem('pendingVerification');
            localStorage.removeItem('tempPassword');
            await signOut(auth);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'email:', error);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      checkEmailVerification();
    }
  }, [isOpen]);

  /**
   * Gère la fermeture du modal et réinitialise les états
   */
  const handleClose = () => {
    setError('');
    setSuccessMessage('');
    setIsSignUp(false);
    setIsForgotPassword(false);
    setAge('');
    setCountry('');
    setCity('');
    setTermsAccepted(false);
    setShowConfirmationPopover(false);
    setAnchorEl(null);
    onClose();
  };

  /**
   * Gère la soumission du formulaire selon le mode actif
   * @param {React.FormEvent} e - Événement du formulaire
   */
  // Mise à jour de la validation du mot de passe à chaque changement
  useEffect(() => {
    if (isSignUp) {
      setPasswordValidation(validatePassword(password));
    }
  }, [password, isSignUp]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      if (isForgotPassword) {
        await sendPasswordResetEmail(auth, email);
        setSuccessMessage(t('auth.resetEmailSent'));
        return;
      }

      if (isSignUp) {
        // Vérifier la validité du mot de passe selon nos critères
        if (!passwordValidation.isValid) {
          setError(t('auth.weakPassword'));
          return;
        }

        if (password !== confirmPassword) {
          setError(t('auth.passwordMismatch'));
          return;
        }

        const parsedAge = parseInt(age, 10);
        if (Number.isNaN(parsedAge) || parsedAge < 13 || parsedAge > 120) {
          setError(t('auth.invalidAge'));
          return;
        }

        const trimmedCountry = country.trim();
        const trimmedCity = city.trim();

        if (!trimmedCountry) {
          setError(t('auth.countryRequired'));
          return;
        }

        if (!trimmedCity) {
          setError(t('auth.cityRequired'));
          return;
        }

        // Afficher le popover au lieu de procéder directement à l'inscription
        setAnchorEl(e.currentTarget as HTMLButtonElement);
        setShowConfirmationPopover(true);
        return;
      }

      // Vérifier si l'email est en attente de vérification
      const pendingEmail = localStorage.getItem('pendingVerification');
      if (pendingEmail === email) {
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;
          if (!user.emailVerified) {
            setError(t('auth.emailNotVerified'));
            await signOut(auth);
            return;
          }
          // Si l'email est vérifié, créer le profil utilisateur avec la langue stockée
          const savedLanguage = (localStorage.getItem('tempLanguage') || 'en') as Language;
          const savedAge = localStorage.getItem('tempAge');
          const savedCountry = localStorage.getItem('tempCountry');
          const savedCity = localStorage.getItem('tempCity');
          await createUserProfile(
            user.uid,
            email,
            savedLanguage,
            savedAge ? parseInt(savedAge, 10) : undefined,
            savedCountry || undefined,
            savedCity || undefined
          );
          // Changer la langue de l'application
          await i18next.changeLanguage(savedLanguage);
          localStorage.removeItem('pendingVerification');
          localStorage.removeItem('tempPassword');
          localStorage.removeItem('tempLanguage');
          localStorage.removeItem('tempAge');
          localStorage.removeItem('tempCountry');
          localStorage.removeItem('tempCity');
        } catch (error) {
          console.error('Erreur lors de la vérification:', error);
          throw error;
        }
      } else {
        // Login normal
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        try {
          // Récupérer le profil utilisateur et sa langue
          const userProfile = await getUserProfile(user.uid);
          if (userProfile?.language) {
            // Changer la langue de l'application
            await i18next.changeLanguage(userProfile.language);
          } else {
            // Utiliser l'anglais par défaut si pas de langue définie
            await i18next.changeLanguage('en');
          }
        } catch (error) {
          console.error('Erreur lors de la récupération de la langue:', error);
          // En cas d'erreur, utiliser l'anglais par défaut
          await i18next.changeLanguage('en');
        }
        await signin(email, password);
        onClose();
      }
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        setError(t('auth.emailAlreadyInUse'));
      } else if (error.code === 'auth/weak-password') {
        setError(t('auth.weakPassword'));
      } else if (error.code === 'auth/invalid-email') {
        setError(t('auth.invalidEmail'));
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError(t('auth.invalidCredentials'));
      } else {
        setError(t('auth.unknownError'));
        console.error(error);
      }
    }
  };

  /**
   * Gère la confirmation de l'inscription après acceptation des conditions
   */
  const handleConfirmSignUp = async () => {
    if (!termsAccepted) {
      setError(t('auth.acceptTerms'));
      return;
    }

    try {
      const user = await signup(email, password);
      await signOut(auth);
      localStorage.setItem('pendingVerification', email);
      localStorage.setItem('tempPassword', password);
      localStorage.setItem('tempLanguage', selectedLanguage);
      localStorage.setItem('tempAge', age.trim());
      localStorage.setItem('tempCountry', country.trim());
      localStorage.setItem('tempCity', city.trim());
      setSuccessMessage(t('auth.signUpSuccess'));
      setIsSignUp(false);
      handleClosePopover();
    } catch (error: any) {
      setError(error.message);
    }
  };

  /**
   * Ferme le popover de confirmation
   */
  const handleClosePopover = () => {
    setShowConfirmationPopover(false);
    setAnchorEl(null);
    setTermsAccepted(false);
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          // >>> Personnalise ici l'enveloppe principale du modal (fond, arrondi, ombre)
          background: 'var(--stat-progress2)',
          borderRadius: '5px',
          boxShadow: '0px 0px 90px rgb(0, 0, 0)',
          color: '#fff',
          overflow: 'hidden', // Maintient les coins arrondis même avec un scroll interne
        }
      }}
    >
      <DialogTitle sx={{ 
        // >>> Zone titre : couleur principale + typo centrale
        color: 'var(--light-green)',
        fontSize: '1.5rem',
        fontWeight: 'bold',
        textAlign: 'center',
        pb: 1
      }}>
        {isForgotPassword 
          ? t('auth.resetPassword')
          : isSignUp 
            ? t('auth.signUp')
            : t('auth.login')
        }
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent
          sx={{
            // Personnalisation de l'ascenseur pour l'intégrer au design existant
            maxHeight: { xs: '70vh', md: '85vh' },
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--light-green) transparent',
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
              borderRadius: '20px',
              border: '12px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, rgba(104,241,209,0.9) 0%, rgba(104,241,209,0.5) 100%)',
              borderRadius: '20px',
              border: '2px solid rgba(16, 18, 30, 0.6)',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'linear-gradient(180deg, rgba(104,241,209,1) 0%, rgba(104,241,209,0.7) 100%)',
            },
            '&::-webkit-scrollbar-corner': {
              background: 'transparent',
            },
          }}
        >
          <Box sx={{
            // >>> Bloc contenant tous les champs + messages
            background: 'var(--fd-gridDreams)',
            borderRadius: '22px',
            p: 3,
            mb: 4,
            boxShadow: 'inset 0 4px 16px rgba(0, 0, 0, 0.8)'
          }}>
            {error && (
              // --- Alertes Erreur ---
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                {error}
              </Alert>
            )}
            {successMessage && (
              // --- Alertes Succès ---
              <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }}>
                {successMessage}
              </Alert>
            )}
            
            {/* ---------------------------------------------------
                Champ EMAIL (ton point d'entrée pour jouer sur les bordures / fond)
               --------------------------------------------------- */}
            <TextField
              autoFocus
              margin="dense"
              id="email"
              label={t('auth.email')}
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(104, 241, 209, 0.8)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#68f1d1',
                  },
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.07)',
                  },
                  '& input': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-focused input': {
                    backgroundColor: 'transparent',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiOutlinedInput-input': {
                  color: '#68f1d1',
                },
                '& .MuiInputBase-input.MuiOutlinedInput-input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px rgba(26, 27, 46, 0.8) inset',
                  WebkitTextFillColor: '#68f1d1',
                },
              }}
            />
            
            {!isForgotPassword && (
              // ---------------------------------------------------
              // Bloc mot de passe (affichage conditionnel hors "mot de passe oublié")
              // ---------------------------------------------------
              <TextField
                margin="dense"
                id="password"
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(104, 241, 209, 0.8)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#68f1d1',
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.07)',
                    },
                    '& input': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-focused input': {
                      backgroundColor: 'transparent',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#68f1d1',
                  },
                  '& .MuiInputBase-input.MuiOutlinedInput-input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 100px rgba(26, 27, 46, 0.8) inset',
                    WebkitTextFillColor: '#68f1d1',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#7fffd4' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            
            {isSignUp && (
              <>
                {/* Indicateur de force du mot de passe */}
                {isSignUp && password.length > 0 && (
                  <PasswordStrengthIndicator validationResult={passwordValidation} />
                )}
                
                {/* ---------------------------------------------------
                    Champ CONFIRMATION mot de passe (design miroir du champ précédent)
                   --------------------------------------------------- */}
                <TextField
                  margin="dense"
                  id="confirmPassword"
                  label={t('auth.confirmPassword')}
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.07)',
                      },
                      '& input': {
                        backgroundColor: 'transparent',
                      },
                      '&.Mui-focused input': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'white',
                    },
                    '& .MuiInputBase-input.MuiOutlinedInput-input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px rgba(26, 27, 46, 0.8) inset',
                      WebkitTextFillColor: 'white',
                    },
                  }}
                />
                {/* ---------------------------------------------------
                    Sélecteur de langue par défaut (impact futur sur UI)
                   --------------------------------------------------- */}
                <FormControl fullWidth margin="normal">
                  <InputLabel id="language-select-label">{t('auth.language')}</InputLabel>
                  <Select
                    labelId="language-select-label"
                    value={selectedLanguage}
                    label={t('auth.language')}
                    onChange={(e) => setSelectedLanguage(e.target.value as Language)}
                  >
                    <MenuItem value="fr">Français</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Español</MenuItem>
                  </Select>
                </FormControl>
                {/* ---------------------------------------------------
                    Champs PROFIL (âge / pays / ville)
                    Ajuster ici spacing, couleurs, placeholders, etc.
                   --------------------------------------------------- */}
                <TextField
                  margin="dense"
                  id="age"
                  label={t('auth.age')}
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  required
                  inputProps={{ min: 13, max: 120 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.07)',
                      },
                      '& input': {
                        backgroundColor: 'transparent',
                      },
                      '&.Mui-focused input': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'white',
                    },
                    '& .MuiInputBase-input.MuiOutlinedInput-input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px rgba(26, 27, 46, 0.8) inset',
                      WebkitTextFillColor: 'white',
                    },
                  }}
                />
                <TextField
                  margin="dense"
                  id="country"
                  label={t('auth.country')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.07)',
                      },
                      '& input': {
                        backgroundColor: 'transparent',
                      },
                      '&.Mui-focused input': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'white',
                    },
                    '& .MuiInputBase-input.MuiOutlinedInput-input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px rgba(26, 27, 46, 0.8) inset',
                      WebkitTextFillColor: 'white',
                    },
                  }}
                />
                <TextField
                  margin="dense"
                  id="city"
                  label={t('auth.city')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.07)',
                      },
                      '& input': {
                        backgroundColor: 'transparent',
                      },
                      '&.Mui-focused input': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'white',
                    },
                    '& .MuiInputBase-input.MuiOutlinedInput-input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px rgba(26, 27, 46, 0.8) inset',
                      WebkitTextFillColor: 'white',
                    },
                  }}
                />
              </>
            )}
          </Box>
          <Box sx={{ 
            // >>> Zone Boutons d'action secondaire (changer de mode, reset, etc.)
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 2,
            mt: 2 
          }}>
            <Box sx={{ 
              // >>> Groupe des boutons "Créer un compte" / "Mot de passe oublié"
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1 
            }}>
              <Button
                onClick={() => {
                  setError('');
                  setSuccessMessage('');
                  setIsForgotPassword(false);
                  setIsSignUp((prev) => {
                    const next = !prev;
                    if (!next) {
                      setAge('');
                      setCountry('');
                      setCity('');
                    }
                    return next;
                  });
                }}
                sx={{
                  color: 'black',
                  background: 'var(--anxiety-low)',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.8)',
                  '&:hover': {
                    background: 'var(--light-green)',
                  }
                }}
              >
                {isSignUp ? t('auth.alreadyHaveAccount') : t('auth.createAccount')}
              </Button>
              {!isSignUp && !isForgotPassword && (
                <Button
                  onClick={() => {
                    setError('');
                    setSuccessMessage('');
                    setIsForgotPassword(true);
                  }}
                  sx={{
                    color: 'black',
                    background: 'var(--cancel-button-gradient)',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.8)',
                    '&:hover': {
                      opacity: 0.8,
                    }
                  }}
                >
                  {t('auth.forgotPassword')}
                </Button>
              )}
            </Box>
            
            <Box sx={{ 
              // >>> Groupe des boutons principaux (Annuler / Valider)
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1 
            }}>
              <Button
                onClick={handleClose}
                sx={{
                  color: 'black',
                  background: 'var(--cancel-button-gradient)',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.8)',
                  '&:hover': {
                    background: 'var(--cancel-button-gradient)',
                    opacity: 0.8
                  }
                }}
              >
                {t('auth.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  background: 'var(--button-gradient)',
                  color: '#1a1b2e',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.8)',
                  '&:hover': {
                    background: 'var(--star-iconsDeg)',
                  }
                }}
              >
                {isForgotPassword 
                  ? t('auth.sendResetLink')
                  : isSignUp 
                    ? t('auth.signUp')
                    : t('auth.login')
                }
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </form>
      <Popover
        open={showConfirmationPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            // >>> Conteneur du popover (conditions d'utilisation)
            maxWidth: 600,
            background:'var(--stat-progress2)',
            maxHeight: '80vh',
            borderRadius: '15px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '18px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(45deg, #556987 30%, #68f1d1 90%)',
              borderRadius: '8px',
              '&:hover': {
                background: 'linear-gradient(45deg, #68f1d1 30%, #556987 90%)',
              },
            },
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* -------------------------------------------------------
              POPUP DE CONFIRMATION : tout est personnalisable
              (typographie, interlignes, icônes, checkboxes)
             ------------------------------------------------------- */}
          <Typography variant="h5" gutterBottom align="center" sx={{ mb: 2 }}>
            {t('auth.confirmationTitle')}
          </Typography>

          <Typography variant="body1" paragraph sx={{ mb: 2 }}>
            {t('auth.mission')}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              {t('auth.notWhatWeAre.title')}
            </Typography>
            <List dense>
              {(t('auth.notWhatWeAre.items', { returnObjects: true }) as string[]).map((item, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Typography sx={{ color: '#ff4b8c', fontSize: '1.2rem' }}>❌</Typography>
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              {t('auth.ethicalRequirement.title')}
            </Typography>
            <Typography variant="body1">
              {t('auth.ethicalRequirement.content')}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              {t('auth.forbiddenContent.title')}
            </Typography>
            <Typography variant="body1">
              {t('auth.forbiddenContent.intro')}
            </Typography>
            <List dense>
              {(t('auth.forbiddenContent.items', { returnObjects: true }) as string[]).map((item, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Typography sx={{ color: '#68f1d1', fontSize: '1.2rem' }}>✓</Typography>
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
            <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
              {t('auth.forbiddenContent.warning')}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              {t('auth.accountCreation.title')}
            </Typography>
            <List dense>
              {(t('auth.accountCreation.items', { returnObjects: true }) as string[]).map((item, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Typography sx={{ color: '#68f1d1', fontSize: '1.2rem' }}>✅</Typography>
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              {t('auth.whyThisRigor.title')}
            </Typography>
            <List dense>
              {(t('auth.whyThisRigor.items', { returnObjects: true }) as string[]).map((item, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              {t('auth.dataProtection.title')}
            </Typography>
            <Typography variant="body1">
              {t('auth.dataProtection.content')}
            </Typography>
            <List dense>
              {(t('auth.dataProtection.items', { returnObjects: true }) as string[]).map((item, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Typography sx={{ color: '#68f1d1', fontSize: '1.2rem' }}>✅</Typography>
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
            {t('auth.teamThanks')}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={t('auth.acceptTermsLabel')}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1,  }}>
            <Button onClick={handleClosePopover} sx={{
                  color: 'black',
                  background: 'var(--cancel-button-gradient)',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.8)',
                  '&:hover': {
                    background: 'var(--cancel-button-gradient)',
                    opacity: 0.8
                  }
                }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmSignUp}
              variant="contained"
              color="primary"
              disabled={!termsAccepted}
            >
              {t('common.continue')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Dialog>
  );
};

export default LoginModal;
